import Vue from 'vue';
import ToastUtils from '@/utils/toast';
import { isValidString } from '../../utils/helper';

class BaseProxy {
  /**
   * The constructor of the BaseProxy.
   *
   * @param {string} endpoint   The endpoint being used.
   * @param {Object} parameters The parameters for the request.
   */
  constructor(endpoint, parameters = {}) {
    this.endpoint = endpoint;
    this.parameters = parameters;
  }

  /**
   * Method used to set the query parameters.
   *
   * @param {Object} parameters The given parameters.
   *
   * @returns {BaseProxy} The instance of the proxy.
   */
  setParameters(parameters) {
    Object.assign(this.parameters, parameters);
    return this;
  }

  /**
   * Method used to set a single parameter.
   *
   * @param {string} parameter The given parameter.
   * @param {*} value The value to be set.
   *
   * @returns {BaseProxy} The instance of the proxy.
   */
  setParameter(parameter, value) {
    this.parameters[parameter] = value;
    return this;
  }

  /**
   * Method used to remove all the parameters.
   *
   * @param {Array} parameters The given parameters.
   *
   * @returns {BaseProxy} The instance of the proxy.
   */
  removeParameters(parameters) {
    parameters.forEach((parameter) => {
      delete this.parameters[parameter];
    });

    return this;
  }

  /**
   * Method used to remove a single parameter.
   *
   * @param {string} parameter The given parameter.
   *
   * @returns {BaseProxy} The instance of the proxy.
   */
  removeParameter(parameter) {
    delete this.parameters[parameter];
    return this;
  }

  /**
   * onSubmitSuccess
   * @param response
   * @param resolve
   * @param options
   * @returns {BaseProxy}
   */
  onSubmitSuccess(response, resolve, options) {
    if (!response.data || options.showResponseHeaders) {
      resolve(response);
    } else if (response.data && (!response.data.data || options.showResponseStatus || options.responseType === 'blob')) {
      resolve(response.data);
    } else {
      resolve(response.data.data);
    }
    return this;
  }

  /**
   * Handle submit error
   * @param {Object} response
   * @param {Function} reject
   * @param {Object} options
   */
  onSubmitError(response, reject, options) {
    if (response && response.data) {
      const { data } = response;
      if (Array.isArray(data.data)) {
        reject(data);
      } else if (options.responseType === 'blob') {
        const reader = new FileReader();
        reader.onload = () => reject(JSON.parse(reader.result));
        reader.readAsText(data);
      } else {
        reject(data);
      }
    } else {
      reject();
    }
    return this;
  }


  /**
   * The method used to perform an AJAX-request.
   *
   * @param {string}      requestType The request type.
   * @param {string}      url         The URL for the request.
   * @param {Object|null} data        The data to be send with the request.
   * @param {Object|null} options     The options to be send with the request.
   *
   * @returns {Promise} The result in a promise.
   */
  submit(requestType, url, data = null, options = null) {
    const defaultOptions = {
      headers: { 'Content-Type': 'application/json' },
    };
    options = Object.assign(defaultOptions, options);

    const promise = new Promise((resolve, reject) => {
      Vue.$http[requestType](url + this.getParameterString(), data, options)
        .then(response => this.onSubmitSuccess(response, resolve, options))
        .catch(({ response }) => this.onSubmitError(response, reject, options));
    });

    if (options.showLoading) {
      const msg = options.loadingMessage || Vue.i18n.t('app.processing');
      this.handleLoadingToast(promise, ToastUtils.showToast('loading', msg));
    }

    this.afterSubmitHandler(promise, options);
    return promise;
  }

  handleLoadingToast(promise, toast) {
    promise.finally(() => {
      toast.goAway(800);
    });
    return this;
  }

  /**
   * afterSubmitHandler
   *
   * Handles actions after a submit request is completed.
   *
   * @param {Promise} submitPromise - The submit request promise.
   * @param {Object|null} options - Options for handling the request.
   *
   * Options:
   *   - {boolean} showLoading - Displays a loading message (default: false).
   *   - {boolean} allowToast - Allows toast notifications (default: true).
   *   - {boolean} showDefErrMsg - Shows the original error message from response (default: false).
   *   - {Object} toast - Configuration for toast messages.
   *   - {string} i18nHandleErr - Custom i18n key for handling error messages.
   *
   * @process
   * 1. Creates a ToastUtils instance with options.
   * 2. Determines wait time for success message based on showLoading.
   * 3. On promise resolution:
   *    - Shows a success toast after the wait time.
   * 4. On promise rejection:
   *    - Displays an error toast based on options or status code.
   *    - If i18nHandleErr is provided, shows localized error messages based on status code.
   *    - If showDefErrMsg is true, shows the original error message from the response.
   *    - If a custom error toast configuration is provided, uses it.
   *    - Otherwise, falls back to default localized error messages based on status code.
   *
   * @returns This for method chaining.
   */

  afterSubmitHandler(submitPromise, options = {}) {
    const toast = new ToastUtils(options.toast || null);
    const waitingTime = options.showLoading ? 500 : 0;
    const allowToast = options.allowToast !== false;
    const showDefaultErrorMessage = options.showDefErrMsg === true;
    const key = isValidString(options.i18nHandleErr) ? options.i18nHandleErr : null;

    submitPromise
      .then(() => {
        if (allowToast) {
          setTimeout(() => toast.show('success'), waitingTime);
        }
      })
      .catch((error) => {
        setTimeout(() => {
          if (allowToast && error && error.status_code) {
            let message = error.message;
            if (key) {
              switch (error.status_code) {
                case 400:
                  message = Vue.i18n.t(`${key}.400`);
                  break;
                default:
                  message = Vue.i18n.t(`${key}.default`);
                  break;
              }
            } else if (showDefaultErrorMessage) {
              message = Vue.i18n.t(error.message);
            } else {
              switch (error.status_code) {
                case 400:
                  message = Vue.i18n.t('error.status.400');
                  break;
                case 401:
                  message = Vue.i18n.t('error.status.401');
                  break;
                default:
                  message = Vue.i18n.t('error.status.default');
                  break;
              }
            }
            toast.show('error', message);
          }
        }, waitingTime);
      });

    return this;
  }

  /**
   * Method used to fetch all items from the API.
   *
   * @returns {Promise} The result in a promise.
   */
  all(options) {
    return this.submit('get', `${this.endpoint}`, null, options);
  }

  /**
   * Method used to fetch a single item from the API.
   *
   * @param {int} id The given identifier.
   *
   * @returns {Promise} The result in a promise.
   */
  find(id, options) {
    return this.submit('get', `${this.endpoint}/${id}`, null, options);
  }

  /**
   * Method used to create an item.
   *
   * @param {Object} item The given item.
   *
   * @returns {Promise} The result in a promise.
   */
  create(item, options) {
    return this.submit('post', `${this.endpoint}`, item, options);
  }

  /**
   * Method used to update an item.
   *
   * @param {int}    id   The given identifier.
   * @param {Object} item The given item.
   *
   * @returns {Promise} The result in a promise.
   */
  update(id, item, options) {
    return this.submit('put', `${this.endpoint}/${id}`, item, options);
  }

  /**
   * Method used to destroy an item.
   *
   * @param {int} id The given identifier.
   *
   * @returns {Promise} The result in a promise.
   */
  destroy(id, options) {
    return this.submit('delete', `${this.endpoint}/${id}`, null, options);
  }

  /**
   * Method used to transform a parameters object to a parameters string.
   *
   * @returns {string} The parameter string.
   */
  getParameterString() {
    const parameterStrings = Object.keys(this.parameters)
      .filter(key => (this.parameters[key] !== null && this.parameters[key] !== undefined && this.parameters[key] !== ''))
      .map(key => `${key}=${encodeURIComponent(this.parameters[key])}`);

    return parameterStrings.length ? `?${parameterStrings.join('&')}` : '';
  }

}

export default BaseProxy;



// WEBPACK FOOTER //
// ./src/store/proxies/base-proxy.js